import { ModelRow, OrderByInfo, StringUtil } from "@mcleod/core";
import { SecondaryFilterProps } from "@mcleod/dispatch";
import { SecondaryFilter } from "@mcleod/powerbroker/src/BrokerageMovementTableType";

export type Filter = (row: ModelRow) => boolean;

const defaultSort: OrderByInfo[] = [{
    field: "customer_name",
    sort: "asc"
},
{
    field: "delivery_date",
    sort: "asc"
}];

interface InvoiceProcessingTableTypeProps {
    caption: string;
    filter: Filter;
    tableIdSuffix: string;
    secondaryFilters?: SecondaryFilter[];
    defaultSort?: OrderByInfo[];
}

export class InvoiceProcessingTableType implements InvoiceProcessingTableTypeProps {
    static readonly FILTER_AGENT: SecondaryFilterProps = {
        id: "agent",
        caption: "Agent",
        filter: (row: ModelRow) => row.get("agent_name") != undefined && row.get("agent_name") != null
    }
    static readonly FILTER_FACTORING_COMPANY: SecondaryFilterProps = {
        id: "factoring_company_name",
        caption: "Factoring Company",
        filter: (row: ModelRow) => row.get("factoring_company_name") != undefined && row.get("factoring_company_name") != null
    }
    static readonly FILTER_QUICK_PAY: SecondaryFilterProps = {
        id: "is_quick_pay",
        caption: "Quick Pay",
        filter: (row: ModelRow) => row.get("is_quick_pay") != undefined && row.get("is_quick_pay") != null && row.get("is_quick_pay") === true
    }
    static readonly FILTER_SPLIT_BILL: SecondaryFilterProps = {
        id: "is_split_pay",
        caption: "Split Invoice",
        filter: (row: ModelRow) => row.get("is_split_pay") != undefined && row.get("is_split_pay") != null && row.get("is_split_pay") === true
    }
    static readonly FILTER_OLDER_THAN_30_DAYS: SecondaryFilterProps = {
        id: "older_than_30_days",
        caption: "Older Than 30 Days",
        filter: (row: ModelRow) => row.get("older_than_30_days") != undefined && row.get("older_than_30_days") != null && (row.get("older_than_30_days") === true)
    }
    static readonly ZERO_BILL: SecondaryFilterProps = {
        id: "zero_bill",
        caption: "Zero Charges",
        filter: (row: ModelRow) => row.get("total_charges") != undefined && row.get("total_charges") != null && (row.get("total_charges").amount <= 0)
    }
    static readonly REBILL: SecondaryFilterProps = {
        id: "rebill",
        caption: "Rebill",
        filter: (row: ModelRow) => row.get("is_rebilled") != undefined && row.get("is_rebilled") != null && (row.get("is_rebilled") === true)
    }
    static readonly FILTER_AUTO_RATE: SecondaryFilterProps = {
        id: "rate_id",
        caption: "Auto Rate",
        imageColor: "success",
        filter: (row: ModelRow) => row.get("rate_id") != undefined && row.get("rate_id") != null && row.get("rate_id") > 0
    }
    static readonly PENDING_REVIEW = new InvoiceProcessingTableType({
        caption: "Pending Review",
        tableIdSuffix: "Pending Review",
        filter: (row) => (!row.get("is_ready_to_process") && StringUtil.isEmptyString(row.get("hold_reason_code"))) ||
        (row.get("total_charges")?.amount == 0) && StringUtil.isEmptyString(row.get("hold_reason_code")),
        secondaryFilters: [
            InvoiceProcessingTableType.FILTER_AGENT,
            InvoiceProcessingTableType.FILTER_FACTORING_COMPANY,
            InvoiceProcessingTableType.FILTER_QUICK_PAY,
            InvoiceProcessingTableType.FILTER_SPLIT_BILL,
            InvoiceProcessingTableType.FILTER_AUTO_RATE,
            InvoiceProcessingTableType.FILTER_OLDER_THAN_30_DAYS,
            InvoiceProcessingTableType.REBILL,
            InvoiceProcessingTableType.ZERO_BILL
        ],
        defaultSort: defaultSort
    });

    static readonly ON_HOLD = new InvoiceProcessingTableType({
        caption: "Hold",
        tableIdSuffix: "Hold",
        filter: (row) => !StringUtil.isEmptyString(row.get("hold_reason_code")),
        secondaryFilters: [
            InvoiceProcessingTableType.FILTER_AGENT,
            InvoiceProcessingTableType.FILTER_FACTORING_COMPANY,
            InvoiceProcessingTableType.FILTER_QUICK_PAY,
            InvoiceProcessingTableType.FILTER_SPLIT_BILL,
            InvoiceProcessingTableType.FILTER_AUTO_RATE,
            InvoiceProcessingTableType.FILTER_OLDER_THAN_30_DAYS,
            InvoiceProcessingTableType.REBILL,
            InvoiceProcessingTableType.ZERO_BILL
        ],
        defaultSort: defaultSort
    });

    static readonly READY_TO_PROCESS = new InvoiceProcessingTableType({
        caption: "Ready to Process",
        tableIdSuffix: "Ready to Process",
        filter: (row) => row.get("is_ready_to_process") && row.get("total_charges")?.amount > 0 &&
                        (row.get("hold_reason_code") === undefined || StringUtil.isEmptyString(row.get("hold_reason_code"))),
        secondaryFilters: [
            InvoiceProcessingTableType.FILTER_AGENT,
            InvoiceProcessingTableType.FILTER_FACTORING_COMPANY,
            InvoiceProcessingTableType.FILTER_QUICK_PAY,
            InvoiceProcessingTableType.FILTER_SPLIT_BILL,
            InvoiceProcessingTableType.FILTER_AUTO_RATE,
            InvoiceProcessingTableType.FILTER_OLDER_THAN_30_DAYS,
            InvoiceProcessingTableType.REBILL,
            InvoiceProcessingTableType.ZERO_BILL
        ],
        defaultSort: defaultSort
    });

    public caption: string;
    public filter: Filter;
    public tableIdSuffix: string;
    public secondaryFilters?: SecondaryFilter[];
    public defaultSort?: OrderByInfo[];


    private constructor(props: Partial<InvoiceProcessingTableTypeProps>) {
        this.caption = props?.caption;
        this.filter = props?.filter;
        this.tableIdSuffix = props?.tableIdSuffix;
        this.secondaryFilters = props?.secondaryFilters;
        this.defaultSort = props?.defaultSort;
    }
}
